import { Box, Chip, Typography, TypographyProps } from '@mui/material';
import React, { ReactNode } from 'react';

export interface SmallHeaderWithIconProps {
  icon: ReactNode;
  title: string;
  chipLabel?: string;
  chipColor?:
    | 'primary'
    | 'secondary'
    | 'success'
    | 'error'
    | 'info'
    | 'warning'
    | 'default';
  iconBackgroundColor?: string;
  /** Tamaño del ícono: 'small' (20px), 'medium' (24px) o valor CSS (ej. '1rem') */
  iconSize?: 'small' | 'medium' | string;
  children?: ReactNode;
  fontWeight?: string | number;
  fontSize?: string;
  variant?: string;
  mb?: string | number;
}

const ICON_SIZE_MAP: Record<string, string> = {
  small: '1.25rem', // 20px, equivalente a MUI fontSize="small"
  medium: '1.5rem', // 24px, equivalente a MUI fontSize="medium"
};

const SmallHeaderWithIcon: React.FC<SmallHeaderWithIconProps> = ({
  icon,
  title,
  chipLabel,
  chipColor = 'primary',
  iconBackgroundColor = 'primary.main',
  iconSize = 'small',
  children,
  fontWeight = 700,
  fontSize = '1rem',
  variant = 'h1',
  mb = 2.5,
}) => {
  const resolvedIconSize = ICON_SIZE_MAP[iconSize] ?? iconSize;

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: mb }}>
      <Box
        sx={{
          backgroundColor: iconBackgroundColor,
          borderRadius: '50%',
          p: 0.5,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: 'white',
        }}
      >
        <Box
          sx={{
            fontSize: resolvedIconSize,
            display: 'flex',
            alignItems: 'center',
            '& svg': {
              fontSize: `${resolvedIconSize} !important`,
            },
          }}
        >
          {icon}
        </Box>
      </Box>
      <Box sx={{ flex: 1 }}>
        <Typography
          variant={variant as TypographyProps['variant']}
          sx={{
            fontWeight: fontWeight,
            color: 'text.primary',
            fontSize: fontSize,
            lineHeight: 1.1,
          }}
        >
          {title}
        </Typography>
        {chipLabel && (
          <Chip
            label={chipLabel}
            size="small"
            color={chipColor}
            variant="outlined"
            sx={{ height: '18px', fontSize: '0.65rem' }}
          />
        )}
      </Box>
      {children}
    </Box>
  );
};

export default SmallHeaderWithIcon;
